import {
  LOCALE_NAMES,
  MS_LANGUAGES_MAP,
  msLanguageIdToLocale,
  parseDbTimestamp,
} from "@repo/shared";
import type {
  LocaleCode,
  MorningstarDocument,
  ProductDocumentEntry,
  ProductDocumentSearchPass,
} from "@repo/shared/types";
import { FileText, RefreshCw, Trash2 } from "lucide-react";
import { useRef, useState, useTransition } from "react";
import { createPortal } from "react-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { postFormData } from "@/lib/api/_client";
import { searchDocument } from "@/lib/api/product-documents";
import {
  useDeleteProductDocument,
  useProductDocumentTypesWithDocs,
  useRefreshMsProductDocument,
  useSaveMsProductDocument,
  useSaveUploadedProductDocument,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { ProductDocumentPickerDialog } from "./product-document-picker-dialog";
import { T } from "./product-documents-tab.i18n";

// A (document type, language section) pair being acted on
type SectionRef = {
  typeId: string;
  language: LocaleCode;
};

// A search whose results await a pick in the multi-match dialog
type PendingSearch = SectionRef & {
  pass: ProductDocumentSearchPass;
};

const sectionKey = (typeId: string, language: string) => `${typeId}:${language}`;

type ProductDocumentsTabProps = {
  productId: string | null;
  productIsin: string | null;
  portfolioCollectionId?: string;
};

export function ProductDocumentsTab({
  productId,
  productIsin,
  portfolioCollectionId = "",
}: ProductDocumentsTabProps) {
  const { tenantDefaultLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const { formatDate } = useRegionalFormat();
  const t = useT(T);

  // Language sections: tenant default first, then the remaining supported languages
  const sectionLanguages: LocaleCode[] = [
    tenantDefaultLanguage,
    ...tenantSupportedLanguages.filter((lang) => lang !== tenantDefaultLanguage),
  ];

  const [isPending, startTransition] = useTransition();
  const {
    documentTypes,
    error: loadError,
    isLoading,
  } = useProductDocumentTypesWithDocs(portfolioCollectionId || null, productId);
  const [searchingKey, setSearchingKey] = useState<string | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const saveMsDocumentMutation = useSaveMsProductDocument();
  const saveUploadedDocumentMutation = useSaveUploadedProductDocument();
  const deleteProductDocumentMutation = useDeleteProductDocument();
  const refreshMsDocumentMutation = useRefreshMsProductDocument();

  // Ref for the hidden file input
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingUpload, setPendingUpload] = useState<SectionRef | null>(null);

  // State for the document picker dialog (several matches, or any fallback result)
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<MorningstarDocument[]>([]);
  const [pendingSearch, setPendingSearch] = useState<PendingSearch | null>(null);

  const handleSearch = async (documentTypeId: string, language: LocaleCode) => {
    if (!productId || !portfolioCollectionId) return;
    setError(null);
    setSuccessMessage(null);
    setSearchingKey(sectionKey(documentTypeId, language));

    startTransition(async () => {
      try {
        const { pass, documents } = await searchDocument(portfolioCollectionId, productId, {
          documentTypeId,
          language,
        });

        if (documents.length === 0) {
          setError(t.noMatchingDocument);
          setSearchingKey(null);
          return;
        }

        // A single hit in the section's own language is attached directly.
        // Fallback (other-language) results always go through the picker, even
        // when there is exactly one — never attach a different language silently.
        if (pass === "language" && documents.length === 1) {
          await saveDocumentHandler(documentTypeId, language, documents[0]!);
          return;
        }

        setPendingDocuments(documents);
        setPendingSearch({ typeId: documentTypeId, language, pass });
        setSelectionDialogOpen(true);
        setSearchingKey(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t.searchFailed);
        setSearchingKey(null);
      }
    });
  };

  const saveDocumentHandler = async (
    documentTypeId: string,
    language: LocaleCode,
    doc: MorningstarDocument,
  ) => {
    if (!productId || !portfolioCollectionId) return;

    try {
      await saveMsDocumentMutation.mutateAsync({
        collectionId: portfolioCollectionId,
        productId,
        data: {
          documentTypeId,
          language,
          msDocument: {
            documentTypeId: doc.documentTypeId,
            language: doc.language,
            market: doc.market,
            documentDate: doc.documentDate,
            downloadUrl: doc.downloadUrl,
          },
        },
      });
      setSuccessMessage(t.documentFoundAndLinked);
      setSearchingKey(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToSaveDocument);
      setSearchingKey(null);
    }
  };

  const handleSelectDocument = async (doc: MorningstarDocument) => {
    if (!pendingSearch) return;

    setSelectionDialogOpen(false);
    setSearchingKey(sectionKey(pendingSearch.typeId, pendingSearch.language));

    startTransition(async () => {
      await saveDocumentHandler(pendingSearch.typeId, pendingSearch.language, doc);
      setPendingDocuments([]);
      setPendingSearch(null);
    });
  };

  const handlePickerOpenChange = (open: boolean) => {
    setSelectionDialogOpen(open);
    if (!open) {
      setPendingDocuments([]);
      setPendingSearch(null);
    }
  };

  // Re-fetch the same document by its stored Morningstar parameters
  const handleRefresh = async (
    documentId: string,
    documentTypeId: string,
    language: LocaleCode,
  ) => {
    if (!productId || !portfolioCollectionId) return;
    setError(null);
    setSuccessMessage(null);
    setSearchingKey(sectionKey(documentTypeId, language));

    startTransition(async () => {
      try {
        await refreshMsDocumentMutation.mutateAsync({
          collectionId: portfolioCollectionId,
          productId,
          documentId,
        });
        setSuccessMessage(t.documentRefreshed);
      } catch (err) {
        setError(err instanceof Error ? err.message : t.searchFailed);
      } finally {
        setSearchingKey(null);
      }
    });
  };

  const handleRemove = async (documentId: string) => {
    if (!productId || !portfolioCollectionId) return;
    setError(null);
    setSuccessMessage(null);

    startTransition(async () => {
      try {
        await deleteProductDocumentMutation.mutateAsync({
          collectionId: portfolioCollectionId,
          productId,
          documentId,
        });
        setSuccessMessage(t.documentRemoved);
      } catch (err) {
        setError(err instanceof Error ? err.message : t.failedToRemoveDocument);
      }
    });
  };

  const handleUploadClick = (documentTypeId: string, language: LocaleCode) => {
    setPendingUpload({ typeId: documentTypeId, language });
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !productId || !pendingUpload) {
      setPendingUpload(null);
      return;
    }

    // Validate file type
    if (file.type !== "application/pdf") {
      setError(t.onlyPdfAllowed);
      setPendingUpload(null);
      e.target.value = "";
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t.fileSizeExceeds);
      setPendingUpload(null);
      e.target.value = "";
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setUploadingKey(sectionKey(pendingUpload.typeId, pendingUpload.language));

    try {
      // Upload file to API
      const uploadData = new FormData();
      uploadData.append("file", file);

      const result = await postFormData<{ success: boolean; url: string; error?: string }>(
        "/internal/uploads/upload-document",
        uploadData,
      );

      if (!result.success) {
        setError(result.error || t.uploadFailed);
        setUploadingKey(null);
        setPendingUpload(null);
        e.target.value = "";
        return;
      }

      // Save the document reference
      await saveUploadedDocumentMutation.mutateAsync({
        collectionId: portfolioCollectionId,
        productId,
        data: {
          documentTypeId: pendingUpload.typeId,
          language: pendingUpload.language,
          fileUrl: result.url,
          fileName: file.name,
        },
      });

      setSuccessMessage(t.documentUploaded);
    } catch (_err) {
      setError(t.uploadFailedRetry);
    } finally {
      setUploadingKey(null);
      setPendingUpload(null);
      e.target.value = "";
    }
  };

  // The actual Morningstar language of a fetched doc, when it differs from its section
  const actualMsLanguageLabel = (doc: ProductDocumentEntry): string | null => {
    if (doc.source !== "morningstar" || !doc.msLanguage) return null;
    if (msLanguageIdToLocale(doc.msLanguage) === doc.language) return null;
    return MS_LANGUAGES_MAP[doc.msLanguage] || doc.msLanguage;
  };

  if (!productId) {
    return <div className="text-center py-8 text-muted-foreground">{t.saveProductFirst}</div>;
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
        {t.failedToLoadDocuments}
      </div>
    );
  }

  if (documentTypes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{t.noDocumentTypesConfigured}</p>
        <p className="text-sm mt-2">{t.goToDocumentsTab}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
      )}

      {successMessage && (
        <div className="text-sm text-success bg-success/10 p-3 rounded-md">{successMessage}</div>
      )}

      <p className="text-sm text-muted-foreground">{t.attachDocumentsDesc}</p>

      <div className="space-y-3">
        {documentTypes.map((docType) => (
          <div key={docType.id} className="border rounded-lg p-4 space-y-3">
            <Label className="text-base font-medium">{localize(docType.nameI18n)}</Label>

            <div className="space-y-2">
              {sectionLanguages.map((lang) => {
                const doc = docType.documents.find((d) => d.language === lang);
                const key = sectionKey(docType.id, lang);
                const isSearching = searchingKey === key;
                const isUploading = uploadingKey === key;
                const actualLanguage = doc ? actualMsLanguageLabel(doc) : null;

                return (
                  <div key={lang} className="border rounded-md p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{LOCALE_NAMES[lang] || lang}</span>
                      {doc && (
                        <Badge variant={doc.source === "morningstar" ? "info" : "secondary"}>
                          {doc.source === "morningstar" ? t.morningstar : t.uploaded}
                        </Badge>
                      )}
                    </div>

                    {doc ? (
                      // Document exists for this language
                      <div className="flex items-center justify-between bg-muted/50 rounded-md p-3">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <div>
                            {doc.source === "morningstar" ? (
                              <>
                                <p className="text-sm font-medium">
                                  {t.documentDate}: {doc.msDocumentDate || t.na}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t.lastSynced}:{" "}
                                  {doc.lastSyncedAt
                                    ? formatDate(parseDbTimestamp(doc.lastSyncedAt))
                                    : t.na}
                                </p>
                              </>
                            ) : (
                              <p className="text-sm font-medium">
                                {doc.uploadedFileName || t.uploadedFile}
                              </p>
                            )}
                            {actualLanguage && (
                              <p className="text-xs text-muted-foreground">
                                {t.language}: {actualLanguage}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {doc.source === "morningstar" && doc.msDownloadUrl && (
                            <Button variant="outline" size="sm" asChild>
                              <a href={doc.msDownloadUrl} target="_blank" rel="noopener noreferrer">
                                {t.open}
                              </a>
                            </Button>
                          )}

                          {doc.source === "upload" && doc.uploadedFileUrl && (
                            <Button variant="outline" size="sm" asChild>
                              <a
                                href={doc.uploadedFileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t.open}
                              </a>
                            </Button>
                          )}

                          {/* Refresh button for Morningstar docs */}
                          {doc.source === "morningstar" && productIsin && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              tooltip={t.refresh}
                              onClick={() => handleRefresh(doc.id, docType.id, lang)}
                              disabled={isPending}
                              loading={isSearching}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Remove button */}
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            tooltip={t.delete}
                            onClick={() => handleRemove(doc.id)}
                            disabled={isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // No document for this language - show actions
                      <div className="flex items-center gap-2">
                        {/* Search Morningstar button - only if mapping configured and product has ISIN */}
                        {docType.msDocumentTypeId && productIsin && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleSearch(docType.id, lang)}
                            disabled={isPending || isUploading}
                            loading={isSearching}
                          >
                            {t.searchMorningstar}
                          </Button>
                        )}

                        {/* Info if no ISIN */}
                        {docType.msDocumentTypeId && !productIsin && (
                          <span className="text-xs text-muted-foreground">{t.addIsinToSearch}</span>
                        )}

                        {/* Info if no MS mapping */}
                        {!docType.msDocumentTypeId && (
                          <span className="text-xs text-muted-foreground">
                            {t.noMsMappingConfigured}
                          </span>
                        )}

                        {/* Upload button - always available */}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isPending || isSearching}
                          onClick={() => handleUploadClick(docType.id, lang)}
                          loading={isUploading}
                        >
                          {t.uploadPdf}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {pendingSearch && (
        <ProductDocumentPickerDialog
          open={selectionDialogOpen}
          onOpenChange={handlePickerOpenChange}
          documents={pendingDocuments}
          pass={pendingSearch.pass}
          sectionLanguage={pendingSearch.language}
          onSelect={handleSelectDocument}
        />
      )}

      {/* Hidden file input rendered via portal to avoid dialog close issues */}
      {typeof document !== "undefined" &&
        createPortal(
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />,
          document.body,
        )}
    </div>
  );
}
